import React, { ReactNode, forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  getCurrentReportingValues,
  getEnvironment,
  isCropNotModelReady,
  isFeatureActiveOnCurrentFarm,
  isImpersonating,
  useGetFarmReportingYear,
  useGetFarms,
  useGetPracticeCrops,
  useGetPracticeSeedings,
  useGetRedzoneExceptions,
  useLocation,
  useNavigate,
  usePracticeManagementCropParameter,
  useTranslation,
} from "utils";

import { FEATURE_FLAG_SLUGS, HarvestUnitValue } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropFormValidationError,
  FieldListField,
  OTHER_REDZONE_EXCEPTION_KEY,
  PRACTICE_TYPE,
  PracticeCrop,
  PracticeSlug,
  RedzoneException,
  WithInjectedName,
  ZERO_UUID,
} from "models";

import {
  AlertBanner,
  Checkbox,
  Flex,
  Form,
  Icon,
  KlimRadioGroup,
  OptGroup,
  Option,
  RadioChangeEvent,
  RangePicker,
  RangerPickerValueType,
  Select,
  Tag,
  TextArea,
  Tooltip,
  Typography,
} from "components";

import { ReviewDraftTag } from "pages/app/all-field-view/components/forms/ReviewDraftTag";
import { CropYieldSection } from "pages/app/all-field-view/components/forms/crop/CropYieldSection";
import { SecondaryDataCropForm } from "pages/app/all-field-view/components/forms/crop/SecondaryDataCropForm";
import { CROP } from "pages/app/all-field-view/constants/cropForm";
import { CropDataToReview } from "pages/app/all-field-view/hooks/useCropDataToReview";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { getCropDateEditDisabled, getIsYieldEditDisabled } from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import {
  InputRequirementConfig,
  getDiverseCoverCropIncentive,
  isRequired,
  isRequiredForSaving,
  shouldShowInput,
} from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";

export type AddEditCropFormProps = {
  baselineYears: { [k in PracticeSlug]?: number[] };
  crop: CropFCPO;
  cropPeriodValidationErrors: CropFormValidationError[];
  // planned data the farmer should confirm now that its date has passed
  dataToReview: CropDataToReview;
  disableTillageAndSeedingInputs: boolean;
  field: FieldListField | null;
  onCropSelectionChanged: (crop: PracticeCrop) => void;
  year: number;
  errors?: CropFormValidationError[];
  recommendedSeedingDate?: string;
  recommendedHarvestDate?: string;
  optionalInputsAreShown: boolean;
  requirementConfig: InputRequirementConfig | null;
  setShowDrawer?: (show: boolean) => void;
  // rendered under the seeding type input, e.g. a verifier's editing request for it
  seedingExtraContent?: ReactNode;
};

export const getFormTitle = (cropType: CROP_TYPE_FCPO) => {
  switch (cropType) {
    case CROP_TYPE_FCPO.MAIN:
      return "cropForm.mainCrop";
    case CROP_TYPE_FCPO.UNDERSOWING:
      return "cropForm.undersowing";
    case CROP_TYPE_FCPO.COVER:
      return "cropForm.coverCrop";
    default:
      return "cropForm.mainCrop";
  }
};

const getCropDateInitialValue = (
  crop: CropFCPO,
  isSoilPlus3DataGapFeatureActive: boolean
): {
  seedingStartDate: moment.Moment | null;
  seedingEndDate: moment.Moment | null;
  harvestStartDate: moment.Moment | null;
  harvestEndDate: moment.Moment | null;
} => {
  if (!crop.id) return { seedingStartDate: null, seedingEndDate: null, harvestStartDate: null, harvestEndDate: null };

  // only show fallback dates if the farm doesn't have the soil+ 3.0 feature flag active (we don't want to show all the missing data requests for all the farms that didn't submit 2025 data)
  return {
    seedingStartDate: crop.seedingStartDate
      ? moment(crop.seedingStartDate)
      : isSoilPlus3DataGapFeatureActive
      ? null
      : moment(crop.fallbackDates.seedingStartDate),
    // seeding end and harvest start are optional, so we don't need to show the fallback dates
    seedingEndDate: crop.seedingEndDate ? moment(crop.seedingEndDate) : null,
    harvestStartDate: crop.harvestStartDate ? moment(crop.harvestStartDate) : null,
    harvestEndDate: crop.harvestEndDate ? moment(crop.harvestEndDate) : isSoilPlus3DataGapFeatureActive ? null : moment(crop.fallbackDates.harvestEndDate),
  };
};

const isOtherRedzoneException = (exceptionId: string | null, redzoneExceptions?: RedzoneException[]) =>
  redzoneExceptions?.some((option) => option.id === exceptionId && option.key === OTHER_REDZONE_EXCEPTION_KEY);

export const AddEditCropForm = forwardRef<HTMLDivElement, AddEditCropFormProps>(
  (
    {
      setShowDrawer,
      seedingExtraContent,
      baselineYears,
      crop,
      cropPeriodValidationErrors,
      dataToReview,
      disableTillageAndSeedingInputs,
      field,
      onCropSelectionChanged,
      recommendedSeedingDate,
      recommendedHarvestDate,
      optionalInputsAreShown,
      year,
      requirementConfig,
    }: AddEditCropFormProps,
    ref
  ) => {
    // stuff from global utils
    const { t } = useTranslation("allFieldView");
    const navigate = useNavigate();
    const location = useLocation();
    const form = Form.useFormInstance();
    const isAdminOrImpersonator = getEnvironment().admin || isImpersonating();
    const env = getEnvironment();

    // stuff from hooks and tanstack-query
    const { isNewCropMix, selectedRecommendationItemOnTimeline, showDrawerFromCMC, setIsNewCropMix, setShowDrawerFromCMC } = useCropMixComposerContext();
    const { submissionStatusValue } = useSubmissionStatus();
    const { currentFarm } = useGetFarms();
    const cropPracticesQuery = useGetPracticeCrops({
      regionId: field?.regionId,
      version: currentFarm?.carbonProgram?.farmLevelFootprinting?.methodology?.version,
    });
    const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
    const { data: redzoneExceptions } = useGetRedzoneExceptions();
    const {
      data: { activeFarmReportingYear },
    } = useGetFarmReportingYear(currentFarm?.id || null);

    // Component state
    const [committedYield, setCommittedYield] = useState<number | null>(crop.yieldPerHa ?? null);
    const [committedYieldUnit, setCommittedYieldUnit] = useState<HarvestUnitValue | null>(crop.yieldPerHaUnit ?? null);
    const [harvestStartDatePickerKey, setHarvestStartDatePickerKey] = useState(0);
    const [isResidueProduced, setIsResidueProduced] = useState<boolean | null>(() => (crop.cropId !== "" ? crop.hasResidue : null));
    const [mandatory, setMandatory] = useState<boolean | null>(crop.mandatory ?? null);
    const [seedingStartDatePickerKey, setSeedingStartDatePickerKey] = useState(0);
    const [selectedCropId, setSelectedCropId] = useState<string>(crop.cropId);
    const [selectedRedzoneExceptionId, setSelectedRedzoneExceptionId] = useState<string | null>(crop.redzoneExceptionId ?? null);

    const cropParameterQuery = usePracticeManagementCropParameter(selectedCropId);

    // stuff from specific utils
    const { isCropSeedingDateEditDisabled, isCropHarvestDateEditDisabled, isCropNameEditDisabled, hasSubmissionForSoilPlus3StartYear } =
      getCropDateEditDisabled({
        crop,
        carbonProgram: currentFarm?.carbonProgram,
        activeFarmReportingYear,
        lastSubmissionDate: submissionStatusValue?.lastSubmissionDate,
        lastSubmissionYear: submissionStatusValue?.lastSubmissionYear,
        isAdmin: isAdminOrImpersonator,
      });

    const getDatePickerDisabledDate = useCallback(
      (crop: CropFCPO, current: moment.Moment, dateType: "seeding" | "harvest"): boolean => {
        if (!crop.isConfirmed || isAdminOrImpersonator) return false;

        const lastSubmissionDate = submissionStatusValue?.lastSubmissionDate ? moment(submissionStatusValue?.lastSubmissionDate) : null;

        const targetDate = dateType === "seeding" ? crop.seedingStartDate : crop.harvestEndDate;
        const isRelevantDate = crop.cropType === CROP_TYPE_FCPO.MAIN ? dateType === "harvest" : dateType === "seeding";
        const newCropSeedingDate = dateType === "seeding" ? current : crop.seedingStartDate;
        const newCropHarvestDate = dateType === "harvest" ? current : crop.harvestEndDate;

        // if there are no seeding and harvest dates, dates are valid only if the year value (db year column in farm_field_crops) doesn't change
        if (!targetDate || crop.growingDatesSoilPlus3DataCollection) {
          // in case the selected date is not the once that determines the crop year
          if (!!crop.year && !isRelevantDate) {
            // the seeding date can be in any year that's same or before the crop year
            if (dateType === "seeding" && current.year() <= crop.year) return false;
            // the harvest date can be in any year that's same or after the crop year
            if (dateType === "harvest" && current.year() >= crop.year) return false;
          }

          const newCropYear = getCropYearFromDates(crop.cropType, moment(newCropSeedingDate), moment(newCropHarvestDate));

          return newCropYear !== crop.year;
        }

        // if there are seeding and harvest dates
        const currentCropYear = getCropYearFromDates(crop.cropType, moment(crop.seedingStartDate), moment(crop.harvestEndDate));
        const newCropYear = getCropYearFromDates(crop.cropType, moment(newCropSeedingDate), moment(newCropHarvestDate));

        // if we can't calculate the year, it means that the date we are looking at is not the relevant one to determine the crop year
        // (harvest dates for main crop or seeding dates for cover and undersowing crops)
        if (!newCropYear) return false;

        //  dates are valid only if the calendar year doesn't change
        if (newCropYear !== currentCropYear) return true;

        // if the last submission date is after the currently selected date, the date is invalid
        // This logic should only apply for data provided before submission of first soil plus 3 year
        if (hasSubmissionForSoilPlus3StartYear && !!lastSubmissionDate && lastSubmissionDate?.isValid() && lastSubmissionDate.isAfter(current, "day"))
          return true;

        return false;
      },
      [submissionStatusValue?.lastSubmissionDate, hasSubmissionForSoilPlus3StartYear, isAdminOrImpersonator]
    );

    // if none of the dates are editable, it means the crop was created and harvested before submission, so the yield should be already confirmed
    const isYieldDisabled = (): boolean =>
      getIsYieldEditDisabled({
        crop,
        isYieldRequired: isRequired(requirementConfig?.yield),
        isCropSeedingDateEditDisabled,
        isCropHarvestDateEditDisabled,
        lastSubmissionYear: submissionStatusValue?.lastSubmissionYear,
      });

    // derived state
    const cropsToDisplay = useMemo<Array<WithInjectedName<PracticeCrop>> | undefined>(() => {
      const crops = cropPracticesQuery.data?.filter((c) => c.enabled);
      if (crop.cropId) {
        const prevSelectedCrop = cropPracticesQuery.data?.find((c) => c.id === crop.cropId);
        if (prevSelectedCrop && !prevSelectedCrop.enabled) crops?.push(prevSelectedCrop);
      }
      return crops;
    }, [cropPracticesQuery.data, crop.cropId]);

    const { endDate: reportingPeriodEnd } = getCurrentReportingValues(activeFarmReportingYear || moment().year(), currentFarm?.carbonProgram);
    const diverseCoverCropIncentive = useMemo(() => getDiverseCoverCropIncentive(currentFarm?.carbonProgram), [currentFarm?.carbonProgram]);
    const harvestDateRange = Form.useWatch([CROP, "harvestDateRange"], form);
    const harvestEndDate = harvestDateRange?.[1]; // Extract end date from range
    const isDraft = harvestEndDate ? moment(harvestEndDate).isAfter(reportingPeriodEnd) : false;
    const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
    const isOtherRedzoneExceptionSelected = mandatory === false && isOtherRedzoneException(selectedRedzoneExceptionId, redzoneExceptions);
    const isSideCropRecommendation = selectedRecommendationItemOnTimeline?.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION;
    const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);
    const cropDateInitialValue = getCropDateInitialValue(crop, isSoilPlus3DataGapFeatureActive);
    const selectedCropData = cropsToDisplay?.find((crop) => crop.id === selectedCropId);
    const isZeroYieldsAllowed = selectedCropData?.harvestIsMandatory === false;
    const showSecondaryDataForm = !env.production && !isInventoryProgram && !crop.isConfirmed && crop.cropType === CROP_TYPE_FCPO.MAIN;

    // event handlers
    const handleResidueProducedChange = useCallback((e: RadioChangeEvent) => {
      setIsResidueProduced(!!e.target.value);
    }, []);

    const handleEditMix = () => {
      if (!location.pathname.includes("cmc")) {
        navigate(`${location.pathname.replace(/\/$/, "")}/cmc/${crop.cropMixId}${location.search}`);
      }
      setShowDrawer?.(false);
      if (showDrawerFromCMC) {
        setShowDrawerFromCMC?.(false);
      }
    };

    // effects
    useEffect(() => {
      const selectedCrop = cropsToDisplay?.find((c) => c.id === selectedCropId);
      if (!selectedCrop) return;
      onCropSelectionChanged(selectedCrop);
    }, [cropsToDisplay, selectedCropId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      // Force the DatePicker to re-evaluate defaultValue by remounting it whenever recommendedSeedingDate or recommendedHarvestDate changes.
      // Since defaultValue is only used for the initial mount and does not update dynamically.

      const seedingDateRangeIsNull = (form.getFieldValue([CROP, "seedingDateRange"]) as RangerPickerValueType).every((item) => item == null);
      const harvestDateRangeIsNull = (form.getFieldValue([CROP, "harvestDateRange"]) as RangerPickerValueType).every((item) => item == null);

      if (seedingDateRangeIsNull) {
        setSeedingStartDatePickerKey((prevState) => prevState + 1);
      }
      if (harvestDateRangeIsNull) {
        setHarvestStartDatePickerKey((prevState) => prevState + 1);
      }
    }, [recommendedSeedingDate, recommendedHarvestDate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (isSideCropRecommendation) return;
      setIsNewCropMix(false);
    }, [setIsNewCropMix, isNewCropMix, isSideCropRecommendation]);

    useEffect(() => {
      setCommittedYield(crop.yieldPerHa ?? null);
    }, [crop.yieldPerHa]);

    // Pre-populate the saved sanityNoteYield, sanityNoteResidue, and wasHarvested
    // fields. Without this, notes whose SanityCheck is not currently visible
    // (e.g. residue note when yield is within range) are absent from the form
    // store and get wiped on submit.
    useEffect(() => {
      form.setFieldValue([CROP, "sanityNoteYield"], crop.sanityNoteYield);
      form.setFieldValue([CROP, "sanityNoteResidue"], crop.sanityNoteResidue);
      form.setFieldValue([CROP, "wasHarvested"], crop.wasHarvested ?? true);
      form.setFieldValue([CROP, "redzoneExceptionExplanation"], crop.redzoneExceptionExplanation);
    }, [crop.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (crop.hasResidue !== null) return;
      if (selectedCropData) {
        // Default residue values should be preselected if no other input has been provided
        setIsResidueProduced(selectedCropData.residueLeftByDefault);
        form.setFieldValue([CROP, "hasResidue"], selectedCropData.residueLeftByDefault);
      }
    }, [selectedCropData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (crop.seedingSelectionType === ZERO_UUID && !selectedCropData?.isPerennial) {
        form.setFieldValue([CROP, "seedingPracticeType"], null);
      }
    }, [selectedCropData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Flex vertical gap="1rem" className="add-edit-crop-form-container">
        {crop.isConfirmed && !isAdminOrImpersonator && (
          <Typography variant="paragraph-m" className="submitted-crop-message">
            {t("cropForm.submittedCropMessage")}
          </Typography>
        )}
        {showSecondaryDataForm && <SecondaryDataCropForm initialValue={crop.isSecondaryData ?? null} />}
        <Flex className="add-edit-crop-form">
          <div className="form-container" data-testid="add-edit-crop-form">
            <Flex justify="space-between">
              <Flex gap="0.75rem">
                <Typography variant="h5" className="margin-bottom-100">
                  {t(getFormTitle(crop.cropType))}
                </Typography>
                {isDraft && (
                  <Tooltip title={t("cropForm.draftTooltip")} style={{ lineHeight: "normal" }}>
                    <Tag dataTestId="timeline-drawer-crop-form-draft-tag" className="chip-pill capitalize margin-0" colour="grey" text={t("cropForm.draft")} />
                  </Tooltip>
                )}
                {crop.cropMixId && (
                  <Flex className="edit-crop-mix-container margin-bottom-100" gap=".125rem" onClick={handleEditMix}>
                    <Typography variant="paragraph-m" className="edit-crop-mix-label">
                      {t("cropForm.editMix")}
                    </Typography>
                    <Icon className="edit-crop-mix-icon" iconName="klim/plant-ai" />
                  </Flex>
                )}
              </Flex>
            </Flex>
            <Form.Item name={[CROP, "cropType"]} hidden={true} initialValue={crop.cropType}>
              {/* we need this to prevent an antd form error; Form.Item needs a valid child */}
              <div />
            </Form.Item>
            {isNewCropMix && (
              <Flex className="new-crop-mix-banner margin-bottom-100" vertical gap="0.5rem">
                <Typography variant="paragraph-m" bold className="new-crop-mix-banner-title">
                  {t("cropForm.newCropMixBanner.title")}
                </Typography>
                <Typography variant="paragraph-m" className="new-crop-mix-banner-description">
                  {t("cropForm.newCropMixBanner.description")}
                </Typography>
              </Flex>
            )}
            {(shouldShowInput(requirementConfig?.cropName, optionalInputsAreShown) || crop) && (
              <>
                <Select
                  dataTestId="add-edit-crop-form-top-crop-selection"
                  label={
                    <Flex align="center">
                      {dataToReview.seedingData && <ReviewDraftTag />}
                      {t("cropForm.cropName")}
                    </Flex>
                  }
                  name={[CROP, "cropId"]}
                  initialValue={crop.cropId ?? null}
                  value={selectedCropId}
                  onChange={(value) => {
                    setSelectedCropId(value);
                    form.setFieldValue([CROP, "sanityNoteYield"], undefined);
                    form.setFieldValue([CROP, "sanityNoteResidue"], undefined);
                    form.setFieldValue([CROP, "wasHarvested"], true);
                  }}
                  showSearch
                  disabled={isCropNameEditDisabled}
                  isLoading={cropPracticesQuery.isFetching}
                  rules={[
                    {
                      required: isRequiredForSaving(requirementConfig?.cropName),
                      message: t("cropForm.errorMessages.cropRequired"),
                    },
                  ]}
                >
                  {crop.cropType === CROP_TYPE_FCPO.MAIN &&
                    cropsToDisplay
                      ?.filter((crop) => crop.isMainCrop)
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      .map((option, index) => (
                        <Option data-testid={`crop-selection-option-main-${index}`} key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      ))}

                  {crop.cropType === CROP_TYPE_FCPO.UNDERSOWING &&
                    cropsToDisplay
                      ?.filter((crop) => crop.isUndersowing)
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      .map((option, index) => (
                        <Option data-testid={`crop-selection-option-undersowing-${index}`} key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      ))}

                  {crop.cropType === CROP_TYPE_FCPO.COVER &&
                    cropsToDisplay
                      ?.filter((crop) => crop.isCatchCrop)
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      .map((option, index) => (
                        <Option data-testid={`crop-selection-option-cover-${index}`} key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      ))}
                </Select>
              </>
            )}

            {isCropNotModelReady(selectedCropData, currentFarm?.carbonProgram) && (
              <AlertBanner
                type="info-highlighted"
                iconName="tabler/info-circle"
                dataTestId="crop-not-model-ready-alert"
                description={t("cropForm.cropNotModelReady")}
                className="margin-top-100"
              />
            )}

            {(shouldShowInput(requirementConfig?.dates, optionalInputsAreShown) || crop) && (
              <Flex gap="1rem" className="margin-top-100 margin-bottom-100">
                <Flex
                  vertical
                  gap="1rem"
                  className="date-picker-container"
                  // aria-disabled is used for unit testing
                  aria-disabled={isCropSeedingDateEditDisabled}
                  data-testid="seeding-date-range-picker-container"
                  data-crop-type={crop.cropType}
                >
                  <RangePicker
                    key={seedingStartDatePickerKey}
                    data-field-id="seedingDateRange"
                    dataTestId={`add-edit-crop-form-seeding-date-range-${crop.cropName}`}
                    initialValue={[cropDateInitialValue.seedingStartDate, cropDateInitialValue.seedingEndDate]}
                    defaultPickerValue={recommendedSeedingDate ? [moment(recommendedSeedingDate), moment(recommendedSeedingDate)] : [null, null]}
                    format={["DD.MM.YYYY"]}
                    label={
                      <Flex align="center">
                        {dataToReview.seedingData && <ReviewDraftTag />}
                        {t("cropForm.seedingDateRange")}
                      </Flex>
                    }
                    name={[CROP, "seedingDateRange"]}
                    rules={[
                      {
                        validator: (_, value: moment.Moment[] | null) => {
                          if (!isRequiredForSaving(requirementConfig?.dates)) return Promise.resolve();
                          if (year < moment().year() && crop.cropId) return Promise.resolve();
                          if (value?.[0] != null && value?.[1] != null) return Promise.resolve();
                          return Promise.reject(new Error(t("cropForm.errorMessages.seedingDateRequired")));
                        },
                      },
                    ]}
                    status={cropPeriodValidationErrors.find((error) => error.fieldId === "seedingStartDate") ? "error" : undefined}
                    // Once submitted, the seeding date for crops can still be changed within the respective farming year (only if the crop seeding date come after the submission date)
                    disabledDate={(current) => getDatePickerDisabledDate(crop, current, "seeding")}
                    disabled={isCropSeedingDateEditDisabled}
                  />
                </Flex>
                <Flex
                  vertical
                  gap="1rem"
                  className="date-picker-container"
                  // aria-disabled is used for unit testing
                  aria-disabled={isCropHarvestDateEditDisabled}
                  data-testid="harvest-date-range-picker-container"
                  data-crop-type={crop.cropType}
                >
                  <RangePicker
                    key={harvestStartDatePickerKey}
                    data-field-id="harvestDateRange"
                    dataTestId={`add-edit-crop-form-harvest-date-range-${crop.cropName}`}
                    format={["DD.MM.YYYY"]}
                    initialValue={[cropDateInitialValue.harvestStartDate, cropDateInitialValue.harvestEndDate]}
                    defaultPickerValue={recommendedHarvestDate ? [moment(recommendedHarvestDate), moment(recommendedHarvestDate)] : [null, null]}
                    name={[CROP, "harvestDateRange"]}
                    label={
                      <Flex align="center">
                        {dataToReview.harvestData && <ReviewDraftTag />}
                        {t("cropForm.harvestDateRange")}
                      </Flex>
                    }
                    rules={[
                      {
                        validator: (_, value: moment.Moment[] | null) => {
                          if (!isRequiredForSaving(requirementConfig?.dates)) return Promise.resolve();
                          if (year < moment().year() && crop.cropId) return Promise.resolve();
                          if (value?.[0] != null && value?.[1] != null) return Promise.resolve();
                          return Promise.reject(new Error(t("cropForm.errorMessages.harvestDateRequired")));
                        },
                      },
                    ]}
                    status={cropPeriodValidationErrors.find((error) => error.fieldId === "harvestEndDate") ? "error" : undefined}
                    // Once submitted, the harvest date can still be changed within the respective farming year (only if the crop harvest date come after the submission date)
                    disabledDate={(current) => getDatePickerDisabledDate(crop, current, "harvest")}
                    disabled={isCropHarvestDateEditDisabled}
                  />
                </Flex>
              </Flex>
            )}

            {(shouldShowInput(requirementConfig?.seedings, optionalInputsAreShown) || crop.seedingPracticeType) && (
              <div ref={ref}>
                <Select
                  dataTestId="add-edit-crop-form-seeding-type-selection"
                  disabled={isRequired(requirementConfig?.seedings) && !crop.seedingPracticeType ? false : disableTillageAndSeedingInputs}
                  initialValue={crop.seedingPracticeType}
                  isLoading={seedingPracticesQuery.isFetching}
                  labelRender={({ label, value }) => (value === ZERO_UUID ? t("practices:seedings.none") : label)}
                  label={
                    <Flex align="center">
                      {dataToReview.seedingType && <ReviewDraftTag />}
                      {t("cropForm.seedingType")}
                      {!!baselineYears?.tillages?.includes(Number(crop.year)) && (
                        <Tag className="chip-pill capitalize margin-left-50 margin-right-0" colour="grey" text={t("cropForm.baseline")} />
                      )}
                    </Flex>
                  }
                  name={[CROP, "seedingPracticeType"]}
                  rules={[{ message: "", required: isRequiredForSaving(requirementConfig?.seedings) }]}
                  showSearch
                  value={crop.seedingPracticeType}
                >
                  <OptGroup>
                    {seedingPracticesQuery.data &&
                      seedingPracticesQuery.data
                        .filter((practice) => selectedCropData?.isPerennial || practice.id !== ZERO_UUID)
                        .sort((a, b) => a.name?.localeCompare(b.name))
                        .map((option, index) => (
                          <Option data-testid={`seeding-selection-option-${index}`} key={option.id} value={option.id}>
                            {option.name}
                          </Option>
                        ))}
                  </OptGroup>
                </Select>
                {seedingExtraContent}
              </div>
            )}

            {shouldShowInput(requirementConfig?.speciesCount, false) &&
              !!diverseCoverCropIncentive?.elementIds?.includes(selectedCropId) &&
              (() => {
                const threshold = diverseCoverCropIncentive?.speciesCountThreshold;
                const defaultChecked = !!(crop.speciesCount && threshold && crop.speciesCount >= threshold);
                return (
                  <Form.Item className="margin-top-50" name={[CROP, "speciesCount"]} initialValue={defaultChecked} valuePropName="checked">
                    <Checkbox>
                      <Typography defaultChecked={defaultChecked} variant="paragraph-m">
                        {t("cropForm.speciesCountThreshold", { count: threshold })}
                      </Typography>
                    </Checkbox>
                  </Form.Item>
                );
              })()}

            {field?.isInRedzone && !isInventoryProgram && crop.cropType === CROP_TYPE_FCPO.COVER && (
              <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
                <Typography variant="paragraph-m">{t("cropForm.cropMandatory")}</Typography>
                <Form.Item
                  name={[CROP, "mandatory"]}
                  // Redzone inputs should always be required for saving if rendered
                  rules={[{ required: true, message: t("modules:bauEntryDrawer.crops.residueLeft.error") }]}
                  initialValue={mandatory}
                >
                  <KlimRadioGroup
                    className="radio-bg-white"
                    size="large"
                    type="tertiary"
                    dataTestId="crop-mandatory-result-toggle"
                    options={[
                      {
                        value: true,
                        label: (
                          <Flex vertical justify="space-around">
                            <Typography variant="paragraph-m">{t("cropForm.yes")}</Typography>
                          </Flex>
                        ),
                      },
                      {
                        value: false,
                        label: (
                          <Flex vertical justify="space-around">
                            <Typography variant="paragraph-m">{t("cropForm.no")}</Typography>
                          </Flex>
                        ),
                      },
                    ]}
                    value={mandatory}
                    onChange={(e) => {
                      setMandatory(e.target.value);
                      if (e.target.value) {
                        form.setFieldValue([CROP, "redzoneExceptionExplanation"], undefined);
                      }
                    }}
                    optionType="button"
                    buttonStyle="solid"
                    fullWidth
                    disabled={!!crop.isConfirmed && !isAdminOrImpersonator}
                  />
                </Form.Item>
              </Flex>
            )}
            {field?.isInRedzone && !isInventoryProgram && crop.cropType === CROP_TYPE_FCPO.COVER && mandatory === false && (
              <Select
                dataTestId="add-edit-crop-form-redzone-exception-type-selection"
                disabled={crop.redzoneExceptionId === null ? false : !!crop.isConfirmed && !isAdminOrImpersonator}
                initialValue={crop.redzoneExceptionId ?? null}
                label={t("cropForm.whyNotMandatory")}
                name={[CROP, "redzoneExceptionId"]}
                onChange={(value) => {
                  setSelectedRedzoneExceptionId(value as string);
                  if (!isOtherRedzoneException(value as string)) {
                    form.setFieldValue([CROP, "redzoneExceptionExplanation"], undefined);
                  }
                }}
                // Redzone inputs should always be required for saving if rendered
                rules={[{ message: "", required: true }]}
                showSearch
              >
                <OptGroup>
                  {redzoneExceptions?.map((option: RedzoneException, index: number) => (
                    <Option data-testid={`redzone-selection-option-${index}`} key={option.id} value={option.id}>
                      {option.description}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            )}
            {isOtherRedzoneExceptionSelected && (
              <TextArea
                className="margin-top-100"
                dataTestId="add-edit-crop-form-redzone-exception-explanation"
                name={[CROP, "redzoneExceptionExplanation"]}
                initialValue={crop.redzoneExceptionExplanation}
                label={t("cropForm.otherReasonExplanation")}
                rules={[{ required: true, message: t("cropForm.otherReasonExplanationRequired") }]}
                autoSize
                disabled={!!crop.isConfirmed && !isAdminOrImpersonator}
              />
            )}
          </div>
        </Flex>
        {(shouldShowInput(requirementConfig?.yield, optionalInputsAreShown) || crop.yieldPerHa !== null) && (
          <CropYieldSection
            label={
              <Flex align="center">
                {dataToReview.harvestData && (isRequired(requirementConfig?.yield) || crop.yieldPerHa != null) && <ReviewDraftTag />}
                <span className={isRequired(requirementConfig?.yield) ? "required" : ""}>{t("cropForm.yield")}</span>
              </Flex>
            }
            initialValue={crop.yieldPerHa ?? null}
            initialValueUnit={crop.yieldPerHaUnit ?? null}
            initialIsHarvested={crop.wasHarvested ?? null}
            disabled={isYieldDisabled()}
            isRequired={isRequired(requirementConfig?.yield)}
            isZeroYieldsAllowed={isZeroYieldsAllowed}
            onChange={(value, valueUnit) => {
              setCommittedYield(value ?? null);
              setCommittedYieldUnit(valueUnit as HarvestUnitValue);
            }}
            committedYield={committedYield}
            committedYieldUnit={committedYieldUnit}
            yieldBenchmarkBoundsTperHa={cropParameterQuery.data?.harvestTonsPerHectare}
            cropName={selectedCropData?.name ?? null}
            initialNoteYield={crop.sanityNoteYield}
          />
        )}
        {/* The residue input does not depend on the requirement configuration because it depends directly on which crop is selected */}
        {selectedCropData && selectedCropData.residue && !selectedCropData.residueHidden && (
          <Flex className="add-edit-crop-form">
            <div className="form-container" data-testid="add-edit-crop-form-residues">
              <Flex align="center" className="margin-bottom-100">
                {dataToReview.harvestData && <ReviewDraftTag />}
                <Typography variant="h5">
                  {t(selectedCropData.residueType ? `cropForm.residue.${selectedCropData.residueType}.title` : "cropForm.residue.title")}
                </Typography>
              </Flex>
              <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
                <Typography variant="paragraph-m">
                  {crop.cropType === CROP_TYPE_FCPO.COVER || crop.cropType === CROP_TYPE_FCPO.UNDERSOWING
                    ? t("cropForm.residue.residueLeftSideCrop")
                    : t(`cropForm.residue.${selectedCropData.residueType ? `${selectedCropData.residueType}.` : ""}residueLeft`)}
                  *
                </Typography>
                <Form.Item
                  name={[CROP, "hasResidue"]}
                  // Residue should always be required for saving
                  rules={[{ required: true, message: t("modules:bauEntryDrawer.crops.residueLeft.error") }]}
                  initialValue={isResidueProduced}
                >
                  <KlimRadioGroup
                    className="radio-bg-white"
                    size="large"
                    type="tertiary"
                    dataTestId="residue-result-toggle"
                    options={[
                      {
                        value: true,
                        label: t("cropForm.yes"),
                      },
                      {
                        value: false,
                        label: t("cropForm.no"),
                      },
                    ]}
                    value={isResidueProduced || false}
                    onChange={handleResidueProducedChange}
                    optionType="button"
                    buttonStyle="solid"
                    fullWidth
                    disabled={!!crop.isConfirmed && !isAdminOrImpersonator}
                  />
                </Form.Item>
              </Flex>
              {selectedCropData.residueLeftByDefault !== null && selectedCropData.residueLeftByDefault !== isResidueProduced && (
                <Flex vertical gap={16}>
                  <AlertBanner
                    type="warning"
                    iconName="tabler/alert-triangle"
                    iconAlign="start"
                    title={t("cropForm.residue.warningTitle")}
                    description={t(
                      selectedCropData.residueType
                        ? `cropForm.residue.${selectedCropData.residueType}.warningDescription`
                        : "cropForm.residue.warningDescription",
                      {
                        cropName: selectedCropData.name,
                        value: isResidueProduced ? t("cropForm.yes") : t("cropForm.no"),
                      }
                    )}
                    headingSize="h5"
                  />
                  <TextArea
                    name={[CROP, "sanityNoteResidue"]}
                    initialValue={crop.sanityNoteResidue}
                    label={t("cropForm.residue.note")}
                    rules={[{ required: true, message: t("cropForm.errorMessages.residueNoteRequired") }]}
                    autoSize
                    disabled={!!crop.isConfirmed && !isAdminOrImpersonator}
                  />
                </Flex>
              )}
            </div>
          </Flex>
        )}
      </Flex>
    );
  }
);
